import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type {
  PolicyComplianceTierPoint,
  RuleDefinition,
  RuleMetric,
  RuleTier,
} from "../lib/api/types";

const AXIS = { fill: "hsl(var(--muted-foreground))", fontSize: 11 };
const TOOLTIP = {
  background: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "0px",
  color: "hsl(var(--popover-foreground))",
  fontSize: 12,
};

/** One color per rule SEVERITY tier — not the credit-grade palette. */
export const RULE_TIER_COLORS: Record<Exclude<RuleTier, "DQ">, string> = {
  A: "hsl(var(--chart-4))",
  B: "hsl(var(--chart-1))",
  C: "hsl(var(--chart-5))",
};

export function ComplianceTrendChart({ series }: { series: PolicyComplianceTierPoint[] }) {
  const lines: { key: keyof PolicyComplianceTierPoint; name: string; color: string }[] = [
    { key: "complianceRateA", name: "Grade A", color: "hsl(var(--chart-2))" },
    { key: "complianceRateB1", name: "Grade B1", color: "hsl(var(--chart-1))" },
    { key: "complianceRateB2", name: "Grade B2", color: "hsl(var(--chart-5))" },
    { key: "complianceRateB3", name: "Grade B3", color: "hsl(var(--chart-4))" },
  ];

  return (
    <div className="h-[420px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={series} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
          <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="period" tick={AXIS} stroke="hsl(var(--border))" />
          <YAxis
            tick={AXIS}
            stroke="hsl(var(--border))"
            width={48}
            domain={[78, 100]}
            tickFormatter={(v: number) => `${v}%`}
          />
          <Tooltip contentStyle={TOOLTIP} formatter={(v: number, name: string) => [`${v}%`, name]} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {lines.map((l) => (
            <Line
              key={l.key}
              type="monotone"
              dataKey={l.key}
              name={l.name}
              stroke={l.color}
              strokeWidth={2}
              dot={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

interface RuleBarDatum {
  ruleId: string;
  label: string;
  ruleTier: Exclude<RuleTier, "DQ">;
  count: number;
}

export function AllRulesBarChart({
  metrics,
  definitions,
}: {
  metrics: RuleMetric[];
  definitions: RuleDefinition[];
}) {
  const byId = new Map(definitions.map((d) => [d.ruleId, d]));
  const data: RuleBarDatum[] = metrics
    .filter((m) => m.ruleId !== "DQ1" && m.ruleId !== "DQ2")
    .map((m) => {
      const def = byId.get(m.ruleId);
      return {
        ruleId: m.ruleId,
        label: `${m.ruleId} · ${def?.label ?? ""}`,
        ruleTier: (def?.ruleTier ?? "A") as Exclude<RuleTier, "DQ">,
        count: m.count,
      };
    })
    .sort((a, b) => b.count - a.count);

  return (
    <div className="space-y-3">
      <div className="h-[420px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 8, right: 32, bottom: 4, left: 8 }}
            barCategoryGap="22%"
          >
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" tick={AXIS} stroke="hsl(var(--border))" />
            <YAxis
              type="category"
              dataKey="label"
              tick={{ ...AXIS, fontSize: 10 }}
              stroke="hsl(var(--border))"
              width={230}
            />
            <Tooltip
              contentStyle={TOOLTIP}
              formatter={(v: number) => [`${v} deals`, "Count"]}
            />
            <Bar dataKey="count" name="Count" radius={[0, 3, 3, 0]}>
              {data.map((d) => (
                <Cell key={d.ruleId} fill={RULE_TIER_COLORS[d.ruleTier]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        {(["A", "B", "C"] as const).map((tier) => (
          <span key={tier} className="inline-flex items-center gap-1.5">
            <span
              className="size-2.5"
              style={{ background: RULE_TIER_COLORS[tier] }}
            />
            Tier {tier}
          </span>
        ))}
      </div>
    </div>
  );
}
